import { textColorFields } from '../_shared/textColor.js';
import { animationFields, mediaRadiusField } from '../_shared/universal.js';
/**
 * featured-collection — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Featured collection" section
 * (shrine-pro-ref/sections/featured-collection.liquid, 87 settings). NOT a reuse of featured-products:
 * this reproduces Shrine's exact control set, groups, defaults and conditional-visibility graph, adapted
 * to our SettingField system + premium-calm token render.
 *
 * Field-type mapping (Shopify → ours): inline_richtext→text, image_picker→image, collection→collection-picker,
 * color→color (custom hex defaults), color_background(gradient)→text (a CSS gradient string — the inspector
 * gradient PICKER is a follow-up), header→`group`, paragraph→`info`. Shopify `visible_if` (== / or / !=) maps
 * to our extended `visibleWhen` (`equals` one-of + `notEquals`). Ids are clean camelCase (the seller only ever
 * sees LABELS); labels/options/defaults are kept faithful to Shrine.
 *
 * Source model: like Shrine, products come from a picked `collection` (host-resolved to its product list),
 * NOT hand-picked blocks — so there are NO blocks. In the builder preview / offline it falls back to demo
 * products so a fresh section always renders.
 */
/** Convenience for the many literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
/** The two word-highlight "engines" are identical except for the field-id prefix + the `none`/default. */
function highlightFields(n) {
    const p = `headingHighlight${n}`;
    const when = (equals) => ({
        field: p,
        equals,
    });
    const fields = [
        {
            id: p,
            type: 'select',
            label: `Heading words highlight ${n}`,
            group: 'General',
            default: n === 1 ? 'solid-color' : 'none',
            info: n === 1
                ? 'Put words in BOLD in the heading to highlight them with this style.'
                : 'Put words in ITALIC in the heading to highlight them with this style.',
            options: [
                ...(n === 2 ? [{ value: 'none', label: 'None' }] : []),
                { value: 'solid-color', label: 'Color' },
                { value: 'gradient-color', label: 'Gradient' },
                { value: 'bg-color', label: 'Background' },
                { value: 'underline-solid', label: 'Underline' },
                { value: 'underline-gradient', label: 'Gradient underline' },
                { value: 'line-1', label: 'Line 1' },
                { value: 'line-2', label: 'Line 2' },
                { value: 'line-3', label: 'Line 3' },
            ],
        },
        {
            id: `${p}Gradient`,
            type: 'gradient',
            label: 'Text gradient',
            group: 'General',
            default: 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)',
            visibleWhen: when('gradient-color'),
        },
        {
            id: `${p}BgColor`,
            type: 'color',
            label: 'Text background color',
            group: 'General',
            default: { ref: 'theme', token: 'accent-2' },
            visibleWhen: when('bg-color'),
        },
        {
            id: `${p}Color`,
            type: 'color',
            label: 'Text color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: when(['solid-color', 'bg-color']),
        },
        {
            id: `${p}Radius`,
            type: 'range',
            label: 'Corner radius',
            group: 'General',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 8,
            visibleWhen: when('bg-color'),
        },
        {
            id: `${p}BorderWidth`,
            type: 'range',
            label: 'Border thickness',
            group: 'General',
            min: 0,
            max: 6,
            step: 1,
            unit: 'px',
            default: 0,
            visibleWhen: when('bg-color'),
        },
        {
            id: `${p}BorderStyle`,
            type: 'select',
            label: 'Border style',
            group: 'General',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: when('bg-color'),
        },
        {
            id: `${p}BorderColor`,
            type: 'color',
            label: 'Border color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: when('bg-color'),
        },
        {
            id: `${p}PaddingBlock`,
            type: 'range',
            label: 'Vertical padding',
            group: 'General',
            info: 'Relative to font size.',
            min: 0,
            max: 1,
            step: 0.1,
            default: 0.3,
            visibleWhen: when('bg-color'),
        },
        {
            id: `${p}PaddingInline`,
            type: 'range',
            label: 'Horizontal padding',
            group: 'General',
            info: 'Relative to font size.',
            min: 0,
            max: 1,
            step: 0.1,
            default: 0.3,
            visibleWhen: when('bg-color'),
        },
        {
            id: `${p}UnderlineThickness`,
            type: 'range',
            label: 'Underline thickness',
            group: 'General',
            min: 0,
            max: 6,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: when(['underline-solid', 'underline-gradient']),
        },
        {
            id: `${p}UnderlineColor`,
            type: 'color',
            label: 'Underline color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: when('underline-solid'),
        },
        {
            id: `${p}UnderlineGradient`,
            type: 'gradient',
            label: 'Underline gradient color',
            group: 'General',
            default: 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)',
            visibleWhen: when('underline-gradient'),
        },
        {
            id: `${p}UnderlinePosition`,
            type: 'range',
            label: 'Vertical position',
            group: 'General',
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 25,
            visibleWhen: when(['underline-solid', 'underline-gradient']),
        },
        {
            id: `${p}LineColor`,
            type: 'color',
            label: 'Stroke color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: when(['line-1', 'line-2', 'line-3']),
        },
        {
            id: `${p}LineWidth`,
            type: 'range',
            label: 'Stroke thickness',
            group: 'General',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 3,
            visibleWhen: when(['line-1', 'line-2', 'line-3']),
        },
        {
            id: `${p}LinePosition`,
            type: 'range',
            label: 'Vertical position',
            group: 'General',
            min: 0,
            max: 100,
            step: 5,
            default: 60,
            visibleWhen: when(['line-1', 'line-2', 'line-3']),
        },
    ];
    return fields;
}
export const featuredCollectionSchema = {
    type: 'featured-collection',
    name: 'Featured collection',
    version: 1,
    templates: ['index', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            default: false,
            info: 'Lets a button link scroll to this section.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── General ───────────────────────────────────────────────────────────────
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: 'General',
            default: 'Featured collection',
            defaultKey: 'section.featured_collection',
        },
        ...highlightFields(1),
        ...highlightFields(2),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'headingSizeDesktop',
            type: 'range',
            label: 'Custom desktop size',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingSizeMobile',
            type: 'range',
            label: 'Custom mobile size',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        { id: 'description', type: 'richtext', label: 'Description', group: 'General' },
        {
            id: 'showDescription',
            type: 'checkbox',
            label: 'Use the collection’s own description',
            group: 'General',
            default: false,
        },
        {
            id: 'descriptionStyle',
            type: 'select',
            label: 'Description style',
            group: 'General',
            default: 'body',
            options: [
                { value: 'body', label: 'Body' },
                { value: 'subtitle', label: 'Subtitle' },
                { value: 'uppercase', label: 'Uppercase' },
            ],
        },
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'General',
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBackground',
            type: 'color',
            label: 'Custom background color',
            group: 'General',
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: 'General',
            info: 'If set, replaces the background color.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: 'General',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: 'General',
            info: 'If empty, the desktop image is used on mobile too.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text color',
            group: 'General',
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customButtonBackground',
            type: 'color',
            label: 'Custom solid button background',
            group: 'General',
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customButtonText',
            type: 'color',
            label: 'Custom solid button label',
            group: 'General',
            default: hex('#ffffff'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'General',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'General',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'General',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'General',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'General',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Collection ────────────────────────────────────────────────────────────
        {
            id: 'sourceMode',
            type: 'select',
            label: 'Source',
            group: 'Collection',
            default: 'single',
            options: [
                { value: 'single', label: 'One collection' },
                { value: 'tabs', label: 'Category tabs (multiple collections)' },
            ],
            info: 'Tabs mode adds a tab bar above the grid, one tab per collection (add “Tab” blocks below).',
        },
        {
            id: 'collection',
            type: 'collection-picker',
            label: 'Collection',
            group: 'Collection',
            info: 'Add a “collection-hidden” tag to a product to hide it from the list.',
            visibleWhen: { field: 'sourceMode', equals: 'single' },
        },
        {
            id: 'tabsAlignment',
            type: 'select',
            label: 'Tab bar alignment',
            group: 'Collection',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
            visibleWhen: { field: 'sourceMode', equals: 'tabs' },
        },
        {
            id: 'productsToShow',
            type: 'range',
            label: 'Number of products to show',
            group: 'Collection',
            min: 2,
            max: 50,
            step: 1,
            default: 4,
        },
        {
            id: 'stretchCards',
            type: 'checkbox',
            label: 'Make all cards the same height',
            group: 'Collection',
            default: false,
        },
        {
            id: 'showViewAll',
            type: 'checkbox',
            label: 'Enable “View all” button',
            group: 'Collection',
            default: true,
        },
        {
            id: 'viewAllStyle',
            type: 'select',
            label: '“View all” style',
            group: 'Collection',
            default: 'solid',
            options: [
                { value: 'link', label: 'Link' },
                { value: 'outline', label: 'Outline' },
                { value: 'solid', label: 'Solid' },
            ],
            visibleWhen: { field: 'showViewAll', equals: true },
        },
        // ── Product cards ─────────────────────────────────────────────────────────
        {
            id: 'cardsColorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Product cards',
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Product cards',
            default: 'adapt',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'portrait', label: 'Portrait' },
                { value: 'square', label: 'Square' },
            ],
        },
        {
            id: 'showSecondaryImage',
            type: 'checkbox',
            label: 'Show second image on hover',
            group: 'Product cards',
            default: false,
        },
        {
            id: 'badges',
            type: 'select',
            label: 'Badges',
            group: 'Badges',
            default: 'regular',
            info: 'Sale shows the discount; Custom uses per-product badge tags.',
            options: [
                { value: 'disabled', label: 'Disabled' },
                { value: 'regular', label: 'Regular sale' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'badgePosition',
            type: 'select',
            label: 'Badge position',
            group: 'Badges',
            default: 'bottom-left',
            options: [
                { value: 'bottom-left', label: 'Bottom left' },
                { value: 'bottom-right', label: 'Bottom right' },
                { value: 'top-left', label: 'Top left' },
                { value: 'top-right', label: 'Top right' },
                { value: 'top-center', label: 'Top center' },
            ],
        },
        {
            id: 'badgeCornerRadius',
            type: 'range',
            label: 'Badge corner radius',
            group: 'Badges',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            // 10px, not 6: the badge grew (see `badgeBaseStyle`) and a 6px corner on the
            // taller plate read as a sharp-cornered sticker. Sections already in a
            // seller's document keep whatever radius they stored.
            default: 10,
        },
        {
            id: 'saleBadgeText',
            type: 'text',
            label: 'Sale badge text',
            group: 'Badges',
            default: 'SAVE [percentage]',
            defaultKey: 'product.save_percentage',
            info: 'Use [percentage] for the percentage saved and [amount] for the money saved.',
            visibleWhen: { field: 'badges', equals: 'regular' },
        },
        {
            id: 'saleBadgeDiscountIcon',
            type: 'checkbox',
            label: 'Display sale badge discount icon',
            group: 'Badges',
            // OFF by default (founder 2026-08-18, reference shot): on the bigger plate the
            // little tag glyph reads as a second, competing mark next to the claim. Shrine
            // shipped it on; the switch is one click away for anyone who wants it back.
            default: false,
            visibleWhen: { field: 'badges', equals: 'regular' },
        },
        {
            id: 'customBadgesList',
            type: 'text',
            label: 'Custom badges list',
            group: 'Badges',
            default: 'BEST DEAL, BEST SELLER, [percentage] OFF',
            defaultKey: 'section.badge_list',
            info: 'Split by comma. A product’s "custom-badge-N" tag shows the Nth entry. [percentage]/[amount] supported.',
            visibleWhen: { field: 'badges', equals: 'custom' },
        },
        {
            id: 'saleBadgeColorScheme',
            type: 'select',
            label: 'Sale badge color',
            group: 'Badges',
            default: 'accent-2',
            visibleWhen: { field: 'badges', notEquals: 'disabled' },
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-2', label: 'Background 2' },
            ],
        },
        {
            id: 'soldOutBadgeColorScheme',
            type: 'select',
            label: 'Sold out badge color',
            group: 'Badges',
            default: 'inverse',
            options: [
                { value: 'background-1', label: 'Background 1' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        {
            id: 'cardTextAlignment',
            type: 'select',
            label: 'Text alignment',
            group: 'Product cards',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'titleLines',
            type: 'select',
            label: 'Limit title to',
            group: 'Product cards',
            default: 'unlimited',
            options: [
                { value: 'unlimited', label: 'Don’t limit' },
                { value: '1', label: '1 line' },
                { value: '2', label: '2 lines' },
                { value: '3', label: '3 lines' },
            ],
        },
        {
            id: 'swatchesOptionName',
            type: 'text',
            label: 'Option name to display swatches on',
            group: 'Color swatches',
            info: 'Name of the variant option (e.g. Color) the on-card swatches are based on. Leave blank to hide.',
        },
        {
            id: 'swatchesPosition',
            type: 'select',
            label: 'Swatches position',
            group: 'Color swatches',
            default: 'top',
            options: [
                { value: 'top', label: 'Top of card information' },
                { value: 'bottom', label: 'Bottom of card information' },
            ],
            visibleWhen: { field: 'swatchesOptionName', notEquals: '' },
        },
        {
            id: 'swatchesDesktopSize',
            type: 'range',
            label: 'Desktop swatch size',
            group: 'Color swatches',
            min: 20,
            max: 60,
            step: 2,
            unit: 'px',
            default: 36,
            visibleWhen: { field: 'swatchesOptionName', notEquals: '' },
        },
        {
            id: 'swatchesBorderRadius',
            type: 'range',
            label: 'Swatch corner radius',
            group: 'Color swatches',
            min: 0,
            max: 100,
            step: 4,
            unit: '%',
            default: 100,
            visibleWhen: { field: 'swatchesOptionName', notEquals: '' },
        },
        {
            id: 'showVendor',
            type: 'checkbox',
            label: 'Show vendor',
            group: 'Product cards',
            default: false,
        },
        {
            id: 'showRating',
            type: 'checkbox',
            label: 'Show product rating',
            group: 'Product cards',
            default: false,
            info: 'Shows the product’s review rating on the card.',
        },
        {
            id: 'enableQuickAdd',
            type: 'checkbox',
            label: 'Enable quick add',
            group: 'Product cards',
            default: false,
        },
        {
            id: 'quickAddFunctionality',
            type: 'select',
            label: 'Quick add button functionality',
            group: 'Product cards',
            default: 'add-to-cart',
            options: [
                { value: 'add-to-cart', label: 'Add to cart / Open details popup' },
                { value: 'link-to-product-page', label: 'Link to product page' },
            ],
            visibleWhen: { field: 'enableQuickAdd', equals: true },
        },
        {
            id: 'quickAddButtonLabel',
            type: 'text',
            label: 'Button label',
            group: 'Product cards',
            default: 'Go to product',
            defaultKey: 'collection.go_to_product',
            visibleWhen: { field: 'quickAddFunctionality', equals: 'link-to-product-page' },
        },
        // ── Color swatches ────────────────────────────────────────────────────────
        {
            id: 'swatchesMobileSize',
            type: 'range',
            label: 'Mobile swatch size',
            group: 'Color swatches',
            min: 14,
            max: 40,
            step: 2,
            unit: 'px',
            default: 24,
            visibleWhen: { field: 'swatchesOptionName', notEquals: '' },
        },
        // ── Slider ────────────────────────────────────────────────────────────────
        {
            id: 'sliderType',
            type: 'select',
            label: 'Type',
            group: 'Slider',
            default: 'slide',
            options: [
                { value: 'slide', label: 'Classic' },
                { value: 'loop', label: 'Infinite' },
            ],
        },
        { id: 'autoplay', type: 'checkbox', label: 'Enable autoplay', group: 'Slider', default: false },
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Autoplay speed',
            group: 'Slider',
            min: 1,
            max: 15,
            step: 0.5,
            unit: 'sec',
            default: 5,
            visibleWhen: { field: 'autoplay', equals: true },
        },
        {
            id: 'arrowsColorScheme',
            type: 'select',
            label: 'Arrows color scheme',
            group: 'Slider',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        {
            id: 'transparentArrows',
            type: 'checkbox',
            label: 'Transparent arrows',
            group: 'Slider',
            default: true,
        },
        {
            id: 'dotsColorScheme',
            type: 'select',
            label: 'Dots color',
            group: 'Slider',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Text' },
            ],
        },
        // ── Desktop ───────────────────────────────────────────────────────────────
        {
            id: 'desktopFullWidth',
            type: 'checkbox',
            label: 'Full page width',
            group: 'Desktop',
            default: false,
        },
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Products per row',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 3,
        },
        {
            id: 'sliderDesktop',
            type: 'checkbox',
            label: 'Enable desktop slider',
            group: 'Desktop',
            default: false,
            info: 'The settings below apply when this is on.',
        },
        {
            id: 'perMoveDesktop',
            type: 'range',
            label: 'Slides to scroll per move',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 1,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Spacing',
            group: 'Desktop',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 40,
        },
        {
            id: 'desktopSidePadding',
            type: 'range',
            label: 'Preview of prev & next slide',
            group: 'Desktop',
            min: 0,
            max: 150,
            step: 5,
            unit: 'px',
            default: 0,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopPaddingCalc',
            type: 'checkbox',
            label: 'Disable empty preview on first & last slide',
            group: 'Desktop',
            default: true,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Desktop',
            default: false,
            info: 'Only when products per row is 1.',
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopDotsPosition',
            type: 'select',
            label: 'Dots position',
            group: 'Desktop',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopArrowsPosition',
            type: 'select',
            label: 'Arrows position',
            group: 'Desktop',
            default: 'sides',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On the sides' },
            ],
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        // ── Mobile ────────────────────────────────────────────────────────────────
        {
            id: 'columnsMobile',
            type: 'select',
            label: 'Number of columns on mobile',
            group: 'Mobile',
            default: '1',
            options: [
                { value: '1', label: '1 column' },
                { value: '2', label: '2 columns' },
            ],
        },
        {
            id: 'sliderMobile',
            type: 'checkbox',
            label: 'Enable mobile slider',
            group: 'Mobile',
            default: true,
            info: 'The settings below apply when this is on.',
        },
        {
            id: 'mobilePreview',
            type: 'checkbox',
            label: 'Preview of prev & next slides',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileDotsPosition',
            type: 'select',
            label: 'Dots position',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileArrowsPosition',
            type: 'select',
            label: 'Arrows position',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On the sides' },
            ],
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        mediaRadiusField,
        // ── Section padding ───────────────────────────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    // Single mode = Shrine (products from the one picked collection, no blocks). Tabs mode adds `tab`
    // blocks — one per category — each pointing at its own collection.
    blocks: [
        {
            type: 'tab',
            name: 'Tab',
            max: 8,
            fields: [
                {
                    id: 'label',
                    type: 'text',
                    label: 'Tab label',
                    default: 'Best selling',
                    defaultKey: 'section.best_selling',
                    maxLength: 40,
                },
                {
                    id: 'collection',
                    type: 'collection-picker',
                    label: 'Collection',
                    info: 'Products shown when this tab is active.',
                },
            ],
        },
    ],
    maxBlocks: 8,
};
export const featuredCollectionPresets = [
    {
        name: 'Featured collection',
        settings: {
            productsToShow: 4,
            columnsDesktop: 3,
            columnsMobile: '2',
            imageRatio: 'adapt',
            badges: 'regular',
            showViewAll: true,
            viewAllStyle: 'solid',
            colorScheme: 'background-1',
            cardsColorScheme: 'background-1',
            paddingTop: 36,
            paddingBottom: 36,
        },
        blocks: [],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const featuredCollectionMeta = {
    label: 'Featured collection',
    group: 'Home',
    icon: 'grid',
};
//# sourceMappingURL=schema.js.map